import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
  PageTitle, KPI, KPIStrip,
  Badge, Button,
  SearchInput,
  Table, THead, TBody, TR, TH, Row, Cell,
  EmptyState, SkeletonRows,
  Avatar, Icons,
  Field, Input,
} from '../shared/ui';

// ── constants ─────────────────────────────────────────────────────────────────
const VEHICLE_TYPES = [
  { value: 'motorbike',    label: 'Motorbike' },
  { value: 'car',          label: 'Car' },
  { value: 'bicycle',      label: 'Bicycle' },
  { value: 'electric_bike',label: 'Electric Bike' },
  { value: 'other',        label: 'Other' },
];

const TYPE_TONE = {
  motorbike:    'primary',
  car:          'success',
  bicycle:      'warning',
  electric_bike:'violet',
  other:        'neutral',
};

const EMPTY_FORM = {
  renter_id: '', license_plate: '', vehicle_type: 'motorbike',
  make: '', model: '', color: '',
};

// ── component ─────────────────────────────────────────────────────────────────
const VehicleManagement = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  // ── queries ──────────────────────────────────────────────────────────────────
  const { data: vehicles = [], isLoading: vehiclesLoading, refetch: refetchVehicles } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => { const r = await api.get('/api/vehicles'); return r.data; },
    select: (data) => Array.isArray(data) ? data : [],
    staleTime: 0,
  });

  const { data: renters = [], isLoading: rentersLoading } = useQuery({
    queryKey: ['renters-list'],
    queryFn: async () => { const r = await api.get('/api/users/renters'); return r.data; },
    select: (data) => Array.isArray(data) ? data : [],
  });

  // ── mutations ────────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: async (data) => { const r = await api.post('/api/vehicles', data); return r.data; },
    onSuccess: () => { toast.success('Vehicle registered successfully'); handleCloseModal(); setTimeout(() => refetchVehicles(), 100); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to register vehicle'),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => { const r = await api.put(`/api/vehicles/${id}`, data); return r.data; },
    onSuccess: () => { toast.success('Vehicle updated successfully'); handleCloseModal(); setTimeout(() => refetchVehicles(), 100); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to update vehicle'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => { const r = await api.delete(`/api/vehicles/${id}`); return r.data; },
    onSuccess: () => { toast.success('Vehicle deleted successfully'); refetchVehicles(); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to delete vehicle'),
  });

  // ── handlers ─────────────────────────────────────────────────────────────────
  const handleOpenModal = (vehicle = null) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      setFormData({
        renter_id: vehicle.renter_id || '',
        license_plate: vehicle.license_plate || '',
        vehicle_type: vehicle.vehicle_type || 'motorbike',
        make: vehicle.make || '', model: vehicle.model || '', color: vehicle.color || '',
      });
    } else {
      setEditingVehicle(null);
      setFormData(EMPTY_FORM);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => { setShowModal(false); setEditingVehicle(null); setFormData(EMPTY_FORM); };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.renter_id)    { toast.error('Please select a renter');      return; }
    if (!formData.license_plate){ toast.error('Please enter license plate');  return; }
    const renterId = parseInt(formData.renter_id, 10);
    const submitData = { ...formData, renter_id: !isNaN(renterId) ? renterId : null };
    if (editingVehicle) {
      updateMutation.mutate({ id: editingVehicle.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDelete = (vehicle) => {
    if (window.confirm(`Delete vehicle ${vehicle.license_plate}?`)) {
      deleteMutation.mutate(vehicle.id);
    }
  };

  const filteredVehicles = vehicles.filter(v =>
    v.license_plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.renter_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.model?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTypeLabel = (type) => VEHICLE_TYPES.find(o => o.value === type)?.label || type;

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <PageTitle
        title="Vehicle Management"
        subtitle="Manage registered vehicles for renters"
        actions={
          <Button kind="primary" size="sm" iconLeft={<Icons.Plus size={14} />} onClick={() => handleOpenModal()}>
            Register Vehicle
          </Button>
        }
      />

      {/* KPI strip */}
      <KPIStrip>
        <KPI label="Total Vehicles"     value={vehicles.length}                                              icon={<Icons.Car size={14} />}   accent="neutral" />
        <KPI label="Motorbikes"         value={vehicles.filter(v => v.vehicle_type === 'motorbike').length}  icon={<Icons.Bike size={14} />}  accent="primary" />
        <KPI label="Cars"               value={vehicles.filter(v => v.vehicle_type === 'car').length}        icon={<Icons.Car size={14} />}   accent="success" />
        <KPI label="Registered Renters" value={renters.length}                                               icon={<Icons.Users size={14} />} accent="info" />
      </KPIStrip>

      {/* Search */}
      <div className="max-w-md">
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by plate, renter, make, model…"
        />
      </div>

      {/* Table */}
      <Table>
        <THead>
          <TR>
            <TH>License Plate</TH>
            <TH>Owner</TH>
            <TH>Type</TH>
            <TH>Make / Model</TH>
            <TH>Color</TH>
            <TH align="right">Actions</TH>
          </TR>
        </THead>
        <TBody>
          {vehiclesLoading ? (
            <SkeletonRows count={5} />
          ) : filteredVehicles.length === 0 ? (
            <tr>
              <td colSpan={6} className="py-10">
                <EmptyState
                  icon={<Icons.Car size={32} />}
                  title={searchTerm ? 'No vehicles found' : 'No vehicles registered'}
                  description={searchTerm ? 'Try adjusting your search term' : 'Register the first vehicle to get started'}
                  primaryAction={
                    !searchTerm && (
                      <Button kind="primary" size="sm" onClick={() => handleOpenModal()}>
                        Register Vehicle
                      </Button>
                    )
                  }
                />
              </td>
            </tr>
          ) : (
            filteredVehicles.map((vehicle) => (
              <Row key={vehicle.id}>
                <Cell>
                  <span className="mono font-medium text-stone-900">{vehicle.license_plate}</span>
                </Cell>
                <Cell>
                  <div className="flex items-center gap-2">
                    <Avatar name={vehicle.renter_name || '?'} size={26} />
                    <span className="text-stone-700 text-sm">{vehicle.renter_name || 'Unknown'}</span>
                  </div>
                </Cell>
                <Cell>
                  <Badge tone={TYPE_TONE[vehicle.vehicle_type] || 'neutral'}>
                    {getTypeLabel(vehicle.vehicle_type)}
                  </Badge>
                </Cell>
                <Cell className="text-stone-700">{vehicle.make} {vehicle.model}</Cell>
                <Cell className="text-stone-700">{vehicle.color}</Cell>
                <Cell align="right">
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => handleOpenModal(vehicle)}
                      className="p-1.5 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                    >
                      <Icons.Edit size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(vehicle)}
                      className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Icons.Trash size={14} />
                    </button>
                  </div>
                </Cell>
              </Row>
            ))
          )}
        </TBody>
      </Table>

      {/* Register / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-stone-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-stone-200 shadow-popover max-w-lg w-full">
            {/* header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
                  <Icons.Car size={18} className="text-teal-600" />
                </div>
                <h2 className="text-base font-semibold text-stone-900">
                  {editingVehicle ? 'Edit Vehicle' : 'Register New Vehicle'}
                </h2>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <Icons.X size={16} />
              </button>
            </div>

            {/* form */}
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Renter */}
              <Field label="Owner (Renter)" required>
                <select
                  value={formData.renter_id}
                  onChange={(e) => setFormData({ ...formData, renter_id: e.target.value })}
                  required
                  className="w-full h-9 px-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
                >
                  <option value="">{rentersLoading ? 'Loading renters…' : 'Select a renter…'}</option>
                  {renters.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.first_name} {r.last_name} ({r.email || r.phone || 'No contact'})
                    </option>
                  ))}
                </select>
                {renters.length === 0 && !rentersLoading && (
                  <p className="text-xs text-amber-600">No renters found. Please add renters first.</p>
                )}
              </Field>

              {/* License plate */}
              <Field label="License Plate" required>
                <Input
                  value={formData.license_plate}
                  onChange={(e) => setFormData({ ...formData, license_plate: e.target.value.toUpperCase() })}
                  placeholder="e.g., 59A-12345"
                  required
                />
              </Field>

              {/* Vehicle type */}
              <Field label="Vehicle Type">
                <select
                  value={formData.vehicle_type}
                  onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                  className="w-full h-9 px-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
                >
                  {VEHICLE_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>

              {/* Make + Model */}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Make">
                  <Input value={formData.make} onChange={(e) => setFormData({ ...formData, make: e.target.value })} placeholder="Honda, Toyota…" />
                </Field>
                <Field label="Model">
                  <Input value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} placeholder="Wave, Camry…" />
                </Field>
              </div>

              {/* Color */}
              <Field label="Color">
                <Input value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} placeholder="Black, White, Red…" />
              </Field>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-3 border-t border-stone-100">
                <Button kind="ghost" type="button" onClick={handleCloseModal}>Cancel</Button>
                <Button
                  kind="primary"
                  type="submit"
                  loading={createMutation.isPending || updateMutation.isPending}
                >
                  {editingVehicle ? 'Update Vehicle' : 'Register Vehicle'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleManagement;
